import React, { ReactChild, useMemo } from "react";
import { ViewMode } from "../../types/public-types";
import { TopPartOfCalendar } from "./top-part-of-calendar";
import {
  getCachedDateTimeFormat,
  getDaysInMonth,
  getLocalDayOfWeek,
  getLocaleMonth,
  getWeekNumberISO8601,
} from "../../helpers/date-helper";
import { DateSetup } from "../../types/date-setup";
import styles from "./calendar.module.css";
import { useTranslation } from "react-i18next";
import dateFormater from "@/helperFuntions/dateFormater";

const ProjectFlag: React.FC<{
  x: number;
  y: number;
  text: string;
  anchor?: "start" | "end";
}> = ({ x, y, text, anchor = "start" }) => {
  const flagWidth = 95;
  const textPadding = 5;

  const rectX = anchor === "start" ? x : x - flagWidth;
  const textX = anchor === "start" ? x + textPadding : x - textPadding;

  return (
    <>
      <rect
        y={y - 2}
        x={rectX}
        width={flagWidth}
        height={20}
        fill="#fab005"
        rx={5}
        ry={5}
      />
      <text
        y={y + 10}
        x={textX}
        fill="#FFFFFF"
        fontSize="12"
        dominantBaseline="middle"
        textAnchor={anchor}
      >
        {text}
      </text>
    </>
  );
};

export type CalendarProps = {
  dateSetup: DateSetup;
  locale: string;
  viewMode: ViewMode;
  rtl: boolean;
  headerHeight: number;
  columnWidth: number;
  fontFamily: string;
  fontSize: string;
  projectStartDate: Date;
  projectEndDate: Date;
  startEndIndicater: boolean;
};

const CalendarComponent: React.FC<CalendarProps> = ({
  dateSetup,
  locale,
  viewMode,
  rtl,
  headerHeight,
  columnWidth,
  fontFamily,
  fontSize,
  projectStartDate,
  projectEndDate,
  startEndIndicater,
}) => {
  const { t } = useTranslation();

  const [topValues, bottomValues] = useMemo(() => {
    const newTopValues: ReactChild[] = [];
    let newBottomValues: ReactChild[] = [];
    const flagY = headerHeight * 0.7;
    let endDateIndex = -1;
    const getCalendarValuesForYear = () => {
      const topDefaultHeight = headerHeight * 0.5;
      for (let i = 0; i < dateSetup.dates.length; i++) {
        const date = dateSetup.dates[i];
        const bottomValue = date.getFullYear();
        if (date.getFullYear() === projectEndDate.getFullYear()) {
          endDateIndex = i;
        }
        newBottomValues.push(
          <svg className={styles.projectStartFlag} key={i}>
            <rect
              x={columnWidth * i}
              y={headerHeight * 0.6}
              width={columnWidth}
              height={headerHeight * 0.38}
              fill="#F9FAFB"
            />
            {date.getFullYear() === projectStartDate.getFullYear() &&
              startEndIndicater && (
                <ProjectFlag
                  x={columnWidth * i - columnWidth * 1.5}
                  y={flagY}
                  text={t("ProjectStart")}
                />
              )}
            {date.getFullYear() === projectEndDate.getFullYear() &&
              startEndIndicater && (
                <ProjectFlag
                  x={columnWidth * (i + 1) + columnWidth / 1.75}
                  y={flagY}
                  text={t("ProjectEnd")}
                  anchor="end"
                />
              )}
              {(startEndIndicater ? i !== endDateIndex && i !== endDateIndex + 1 :true) && (
            <text
              y={headerHeight * 0.8}
              x={columnWidth * i + columnWidth * 0.5}
              className={styles.calendarBottomText}
              dominantBaseline="middle"
              textAnchor="middle"
            >
              {bottomValue}
            </text>)}
          </svg>
        );
        if (
          i === 0 ||
          date.getFullYear() !== dateSetup.dates[i - 1].getFullYear()
        ) {
          const topValue = date.getFullYear().toString();
          const yearWidth =
            (365 / dateSetup.dates.length) *
            columnWidth *
            dateSetup.dates.length;
          const xText = columnWidth * i + yearWidth / 2;

          newTopValues.push(
            <TopPartOfCalendar
              key={topValue}
              value={topValue}
              x1Line={columnWidth * i}
              y1Line={0}
              y2Line={headerHeight}
              xText={xText}
              yText={topDefaultHeight * 0.9}
            />
          );
        }
      }
    };

    const getCalendarValuesForQuarterYear = () => {
      const topDefaultHeight = headerHeight * 0.5;
      for (let i = 0; i < dateSetup.dates.length; i++) {
        const date = dateSetup.dates[i];
        const quarter = "Q" + Math.floor((date.getMonth() + 3) / 3);
        newBottomValues.push(
          <svg key={date.getTime()}>
<rect
              x={columnWidth * i}
              y={headerHeight * 0.6}
              width={columnWidth}
              height={headerHeight * 0.38}
              fill="#F9FAFB"
            />
          <text
            key={date.getTime()}
            y={headerHeight * 0.8}
            x={columnWidth * i + columnWidth * 0.5}
            className={styles.calendarBottomText}
            dominantBaseline="middle"
            textAnchor="middle"
            >
            {quarter}
          </text>
            </svg>
        );
        if (
          i === 0 ||
          date.getFullYear() !== dateSetup.dates[i - 1].getFullYear()
        ) {
          const topValue = date.getFullYear().toString();
          const xText =
            columnWidth * i +
            (columnWidth *
              dateSetup.dates.filter(
                (d) => d.getFullYear() === date.getFullYear()
              ).length) /
              2;
          newTopValues.push(
            <TopPartOfCalendar
              key={topValue}
              value={topValue}
              x1Line={columnWidth * i}
              y1Line={0}
              y2Line={topDefaultHeight}
              xText={xText}
              yText={topDefaultHeight * 0.9}
            />
          );
        }
      }
    };

    const getCalendarValuesForMonth = () => {
      const topDefaultHeight = headerHeight * 0.5;
      for (let i = 0; i < dateSetup.dates.length; i++) {
        const date = dateSetup.dates[i];
        const bottomValue = getLocaleMonth(date, locale);
        if (
          date.getFullYear() === projectEndDate.getFullYear() &&
          date.getMonth() === projectEndDate.getMonth()
        ) {
          endDateIndex = i;
        }
        newBottomValues.push(
          <svg className={styles.projectStartFlag} key={i}>
          <rect
              x={columnWidth * i}
              y={headerHeight * 0.6}
              width={columnWidth}
              height={headerHeight * 0.38}
              fill="#F9FAFB"
            />
            {date.getFullYear() === projectStartDate.getFullYear() &&
              date.getMonth() === projectStartDate.getMonth() &&
              startEndIndicater && (
                <ProjectFlag
                  x={columnWidth * i - columnWidth * 1.5}
                  y={flagY}
                  text={t("ProjectStart")}
                />
              )}
            {date.getFullYear() === projectEndDate.getFullYear() &&
              date.getMonth() === projectEndDate.getMonth() &&
              startEndIndicater && (
                <ProjectFlag
                  x={columnWidth * (i + 1) + columnWidth / 1.75}
                  y={flagY}
                  text={t("ProjectEnd")}
                  anchor="end"
                />
              )}
              {(startEndIndicater ? i !== endDateIndex && i !== endDateIndex + 1 :true) && (
            <text
              key={bottomValue + date.getFullYear()}
              y={headerHeight * 0.8}
              x={columnWidth * i + columnWidth * 0.5}
              className={styles.calendarBottomText}
              dominantBaseline="middle"
              textAnchor="middle"
            >
              {bottomValue}
            </text>)}
          </svg>
        );
        if (
          i === 0 ||
          date.getFullYear() !== dateSetup.dates[i - 1].getFullYear()
        ) {
          const topValue = date.getFullYear().toString();
          const yearWidth =
            columnWidth *
            dateSetup.dates.filter(
              (d) => d.getFullYear() === date.getFullYear()
            ).length;
          const xText = columnWidth * i + yearWidth / 2;

          newTopValues.push(
            <TopPartOfCalendar
              key={topValue}
              value={topValue}
              x1Line={columnWidth * i}
              y1Line={0}
              y2Line={topDefaultHeight}
              xText={xText}
              yText={topDefaultHeight * 0.9}
            />
          );
        }
      }
    };

    const getCalendarValuesForWeek = () => {
      const topDefaultHeight = headerHeight * 0.5;
      const dates = dateSetup.dates;
      let weeksCount = 1;

      for (let i = dates.length - 1; i >= 0; i--) {
        const date = dates[i];
        let topValue = "";
        if (i === 0 || date.getMonth() !== dates[i - 1].getMonth()) {
          topValue = `${getLocaleMonth(date, locale)}, ${date.getFullYear()}`;
        }
        if (
          getWeekNumberISO8601(date) === getWeekNumberISO8601(projectEndDate) &&
          date.getFullYear() === projectEndDate.getFullYear()
        ) {
          endDateIndex = i;
        }
        const bottomValue = `W${getWeekNumberISO8601(date)}`;
        newBottomValues.push(
          <svg className={styles.projectStartFlag} key={i}>
            <rect
              x={columnWidth * i}
              y={headerHeight * 0.6}
              width={columnWidth}
              height={headerHeight * 0.38}
              fill="#F9FAFB"
            />
            {getWeekNumberISO8601(date) ===
              getWeekNumberISO8601(projectStartDate) &&
              date.getFullYear() === projectStartDate.getFullYear() &&
              startEndIndicater && (
                <ProjectFlag
                  x={columnWidth * i - columnWidth * 2.5}
                  y={flagY}
                  text={t("ProjectStart")}
                />
              )}
            {getWeekNumberISO8601(date) ===
              getWeekNumberISO8601(projectEndDate) &&
              date.getFullYear() === projectEndDate.getFullYear() &&
              startEndIndicater && (
                <ProjectFlag
                  x={columnWidth * (i + 1) + columnWidth / 1.75}
                  y={flagY}
                  text={t("ProjectEnd")}
                  anchor="end"
                />
              )}
              {(startEndIndicater ? i !== endDateIndex && i !== endDateIndex + 1 :true) && (
            <text
              y={headerHeight * 0.8}
              x={columnWidth * i + columnWidth * 0.5}
              className={styles.calendarBottomText}
              dominantBaseline="middle"
              textAnchor="middle"
            >
              {bottomValue}
            </text>)}
          </svg>
        );

        if (topValue) {
          if (i !== dates.length - 1) {
            newTopValues.push(
              <TopPartOfCalendar
                key={topValue}
                value={topValue}
                x1Line={columnWidth * i + weeksCount * columnWidth}
                y1Line={0}
                y2Line={topDefaultHeight}
                xText={columnWidth * i + (columnWidth * weeksCount) / 2}
                yText={topDefaultHeight * 0.9}
              />
            );
          }
          weeksCount = 0;
        }
        weeksCount++;
      }
    };

    const getCalendarValuesForDay = () => {
      const topDefaultHeight = headerHeight * 0.5;
      const dates = dateSetup.dates;
      for (let i = 0; i < dates.length; i++) {
        const date = dates[i];
        const bottomValue = `${getLocalDayOfWeek(date, locale, "short")}, ${date
          .getDate()
          .toString()}`;
        if (dateFormater(date) === dateFormater(projectEndDate)) {
          endDateIndex = i;
        }
        newBottomValues.push(
          <g className={styles.projectStartFlag} key={i}>
             <rect
              x={columnWidth * i}
              y={headerHeight * 0.6}
              width={columnWidth}
              height={headerHeight * 0.38}
              fill="#F9FAFB"
            />
            {dateFormater(date) === dateFormater(projectStartDate) &&
              startEndIndicater && (
                <ProjectFlag
                  x={columnWidth * i - columnWidth * 2.5}
                  y={flagY}
                  text={t("ProjectStart")}
                />
              )}
            {dateFormater(date) === dateFormater(projectEndDate) &&
              startEndIndicater && (
                <ProjectFlag
                  x={columnWidth * (i + 1) + columnWidth / 1.75}
                  y={flagY}
                  text={t("ProjectEnd")}
                  anchor="end"
                />
              )}
            {  (startEndIndicater ? i !== endDateIndex && i !== endDateIndex + 1 :true) && (
              
              <text
                y={headerHeight * 0.8}
                x={columnWidth * i + columnWidth * 0.5}
                className={styles.calendarBottomText}
                dominantBaseline="middle"
                textAnchor="middle"
              >
                {bottomValue}
              </text>
            )}
          </g>
        );
        if (
          i + 1 !== dates.length &&
          date.getMonth() !== dates[i + 1].getMonth()
        ) {
          const topValue = getLocaleMonth(date, locale);
          newTopValues.push(
            <TopPartOfCalendar
              key={topValue + date.getFullYear()}
              value={topValue}
              x1Line={columnWidth * (i + 1)}
              y1Line={0}
              y2Line={topDefaultHeight - 10}
              xText={
                columnWidth * (i + 1) -
                (getDaysInMonth(date.getMonth(), date.getFullYear()) *
                  columnWidth) /
                  2
              }
              yText={topDefaultHeight * 0.9 - 10}
            />
          );
        }
      }
    };

    const getCalendarValuesForPartOfDay = () => {
      const ticks = viewMode === ViewMode.HalfDay ? 2 : 4;
      const topDefaultHeight = headerHeight * 0.5;
      const dates = dateSetup.dates;
      for (let i = 0; i < dates.length; i++) {
        const date = dates[i];
        const bottomValue = getCachedDateTimeFormat(locale, {
          hour: "numeric",
        }).format(date);

        newBottomValues.push(
          <svg key={date.getTime()}>
<rect
              x={columnWidth * i}
              y={headerHeight * 0.6}
              width={columnWidth}
              height={headerHeight * 0.38}
              fill="#F9FAFB"
            />
          <text
            y={headerHeight * 0.8}
            x={columnWidth * (i + +rtl) + columnWidth * 0.5}
            className={styles.calendarBottomText}
            fontFamily={fontFamily}
            dominantBaseline="middle"
            textAnchor="middle"
            >
            {bottomValue}
          </text>
            </svg>
        );
        if (i === 0 || date.getDate() !== dates[i - 1].getDate()) {
          const topValue = `${getLocalDayOfWeek(
            date,
            locale,
            "short"
          )}, ${date.getDate()} ${getLocaleMonth(date, locale)}`;
          newTopValues.push(
            <TopPartOfCalendar
              key={topValue + date.getFullYear()}
              value={topValue}
              x1Line={columnWidth * i}
              y1Line={0}
              y2Line={topDefaultHeight}
              xText={columnWidth * i + (ticks * columnWidth) / 2}
              yText={topDefaultHeight * 0.9}
            />
          );
        }
      }
    };

    const getCalendarValuesForHour = () => {
      const topDefaultHeight = headerHeight * 0.5;
      const dates = dateSetup.dates;
      for (let i = 0; i < dates.length; i++) {
        const date = dates[i];
        const bottomValue = getCachedDateTimeFormat(locale, {
          hour: "numeric",
        }).format(date);

        newBottomValues.push(
          <svg key={date.getTime()}>
            <rect
              x={columnWidth * i}
              y={headerHeight * 0.6}
              width={columnWidth}
              height={headerHeight * 0.38}
              fill="#F9FAFB"
            />
            <text
              y={headerHeight * 0.8}
              x={columnWidth * i + columnWidth * 0.5}
              className={styles.calendarBottomText}
              fontFamily={fontFamily}
              dominantBaseline="middle"
              textAnchor="middle"
            >
              {bottomValue}
            </text>
          </svg>
        );
        if (i !== 0 && date.getDate() !== dates[i - 1].getDate()) {
          const displayDate = dates[i - 1];
          const topValue = `${getLocalDayOfWeek(
            displayDate,
            locale,
            "long"
          )}, ${displayDate.getDate()} ${getLocaleMonth(displayDate, locale)}`;
          const topPosition = (date.getHours() - 24) / 2;
          newTopValues.push(
            <TopPartOfCalendar
              key={topValue + displayDate.getFullYear()}
              value={topValue}
              x1Line={columnWidth * i}
              y1Line={0}
              y2Line={topDefaultHeight}
              xText={columnWidth * (i + topPosition)}
              yText={topDefaultHeight * 0.9}
            />
          );
        }
      }
    };

    switch (viewMode) {
      case ViewMode.Year:
        getCalendarValuesForYear();
        break;
      case ViewMode.QuarterYear:
        getCalendarValuesForQuarterYear();
        break;
      case ViewMode.Month:
        getCalendarValuesForMonth();
        break;
      case ViewMode.Week:
        getCalendarValuesForWeek();
        newBottomValues = newBottomValues.reverse();
        break;
      case ViewMode.Day:
        getCalendarValuesForDay();
        break;
      case ViewMode.QuarterDay:
      case ViewMode.HalfDay:
        getCalendarValuesForPartOfDay();
        break;
      case ViewMode.Hour:
        getCalendarValuesForHour();
        break;
    }

    return [newTopValues, newBottomValues];
  }, [
    dateSetup,
    locale,
    viewMode,
    rtl,
    headerHeight,
    columnWidth,
    fontFamily,
    fontSize,
    projectStartDate,
    projectEndDate,
    startEndIndicater,
    t,
  ]);

  return (
    <g
      className="calendar sticky top-0 font-[600] !text-gray-200/50 select-none"
      fontSize={fontSize}
      fontFamily={fontFamily}
    >
      <rect
        x={0}
        y={0}
        width={columnWidth * dateSetup.dates.length}
        height={headerHeight}
        className={styles.calendarHeader + " !select-none ![&>*]:select-none"}
      />
      {bottomValues} {topValues}
    </g>
  );
};

export const Calendar = React.memo(CalendarComponent);
